/**
 * Issue #183 — which basemap the grounds maps draw, and remembering it.
 *
 * The ask (Martin 8 Aug, Andreas 13 Aug, Martin again 14 Aug): the grounds
 * map must be viewable as street map *and* satellite. Satellite is not a
 * preference here, it is what makes a cricket ground identifiable — you pin
 * the actual square or a net block on imagery; on a street map a park is a
 * green rectangle. So this is load-bearing for the click-to-drop-a-pin add
 * flow, for #131's mis-pinned corrections, and for #175/#176 nets.
 *
 * Three things live here rather than in `GroundsMap.jsx`:
 *
 *  1. **The tile definitions**, so the two maps on that page (the directory
 *     map and the ground-detail dialog) cannot drift into showing different
 *     imagery under the same label, and so the attribution travels with the
 *     URL it belongs to. The switcher itself is leaflet's own
 *     `LayersControl.BaseLayer`, deliberately not hand-rolled: leaflet swaps
 *     the attribution control's contents when IT changes the base layer, and
 *     that is the mechanism issue #183 point 2 asks for. Swapping a
 *     `<TileLayer url>` by hand would leave the previous source's credit on
 *     screen — an attribution bug, not a cosmetic one.
 *
 *  2. **The persistence.** Deliberately not inline in `GroundsMap.jsx`:
 *     `nearMe.test.js` asserts that file contains no browser-storage
 *     primitive at all, because issue #134's privacy claim is that the
 *     device position is never written down. That assertion is worth more
 *     than the convenience of a two-line storage call in the page, so the
 *     page imports `readBasemap`/`writeBasemap` and the only storage call in
 *     the grounds surface is here, holding a value that is a map style and
 *     can never be a position.
 *
 *  3. **The per-surface default**, which is the one non-obvious decision.
 *
 * ## One preference, two defaults — which now agree
 *
 * There is a single stored preference (`nw_basemap`) and both maps read it,
 * so a user who picks street on the directory map opens a ground and gets
 * street there too — issue #183 point 4 is explicit that a setting which
 * resets when the dialog opens "reads as broken".
 *
 * What each surface does with NO stored choice is its own constant, and as
 * of issue #380 both say Satellite:
 *
 *   - the detail dialog has opened on imagery since #58, and Martin asked
 *     for it by name on 14 Aug: "when you click on a saved ground it should
 *     show a satellite image of the ground";
 *   - the directory map opened on Street (#183 point 1) until #380, where
 *     Martin asked for satellite there too — "Make Map Satellite view the
 *     default" — and the reason is the same one that made the dialog
 *     satellite: you recognise a cricket ground by seeing the square and the
 *     boundary, and on street rendering a park is a green rectangle.
 *
 * #380 changed a DEFAULT and nothing else. The toggle and its persistence
 * are #183's and stay exactly as they were: street is one click away, an
 * explicit choice still overrides both fallbacks in both directions, and a
 * returning user's stored street preference still beats this default —
 * which is what makes it one preference rather than two. The imagery is
 * unchanged too (the Esri layer that has been in the app since #58); which
 * licensed source it should ultimately be is issue #361's open decision and
 * a one-line url swap below whenever it lands.
 *
 * The two constants are kept apart even though they now hold the same value:
 * they answer different questions ("what does the directory show a new
 * user", "what does the dialog show them"), #361 or a later ask could split
 * them again, and collapsing them would make the next divergence a
 * refactor rather than an edit.
 */

/** The stored key. `nw_` is this app's prefix for view preferences
 *  (`nw_stats_season`, `nw_perf_comp`, `nw_text_size`). */
export const BASEMAP_STORAGE_KEY = "nw_basemap";

/**
 * The basemaps, in the order the control lists them.
 *
 * `label` is what leaflet shows AND the key it reports back on
 * `baselayerchange` — leaflet's event carries the layer's NAME, not our id —
 * so `basemapIdFromLabel` below is the only place that mapping lives.
 */
export const BASEMAPS = [
  {
    id: "street",
    label: "Street",
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    attribution: '&copy; <a href="https://osm.org/copyright">OpenStreetMap</a>',
    maxZoom: 19,
  },
  {
    id: "satellite",
    label: "Satellite",
    // Already in the app (the detail dialog has rendered these tiles since
    // #58) so this issue adds no new imagery source — it makes the source
    // that was there selectable. The licence basis for using it as a
    // default-visible basemap is tracked separately; see issue #183.
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    // Names the layer and links to the service that serves it, rather than
    // the bare "Tiles © Esri" this replaces — the service's own metadata
    // carries the full mosaic credit list. Measured rather than assumed:
    // that full list (Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping,
    // Aerogrid, IGN, IGP, UPR-EGP and the GIS User Community) wraps to three
    // lines at 390px and swallows ~90px of the ground-detail dialog's 280px
    // map — a third of the imagery, to say in full what a link says.
    attribution:
      'Tiles &copy; <a href="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer">Esri World Imagery</a>',
    maxZoom: 19,
  },
];

/**
 * Fallback for the grounds DIRECTORY map when nothing has been chosen.
 * Satellite since issue #380 (was street, #183 point 1).
 */
export const DIRECTORY_DEFAULT_BASEMAP = "satellite";

/** Fallback for the ground DETAIL dialog map when nothing has been chosen. */
export const DETAIL_DEFAULT_BASEMAP = "satellite";

const isKnown = (id) => BASEMAPS.some((b) => b.id === id);

/**
 * The stored choice, or `null` when there isn't one.
 *
 * `null` is a real state and not the same as "street": it is what lets the
 * two maps keep different defaults until the user has actually expressed a
 * preference. An unreadable store (Safari private mode throws on access) and
 * a value we no longer recognise (a basemap removed in a later release) both
 * read as "no choice" rather than as an error — a broken preference must
 * never be able to stop a map drawing.
 */
export function readBasemap() {
  try {
    const stored = window.localStorage.getItem(BASEMAP_STORAGE_KEY);
    return isKnown(stored) ? stored : null;
  } catch {
    return null;
  }
}

/**
 * Record a choice. Returns whether it was stored — writing can fail (quota,
 * private mode) and the caller still holds the choice in component state, so
 * the toggle keeps working for the session even when nothing persists.
 */
export function writeBasemap(id) {
  if (!isKnown(id)) return false;
  try {
    window.localStorage.setItem(BASEMAP_STORAGE_KEY, id);
    return true;
  } catch {
    return false;
  }
}

/** The basemap a surface should show: the choice if there is one, else its own default. */
export function resolveBasemap(chosen, fallback) {
  if (isKnown(chosen)) return chosen;
  return isKnown(fallback) ? fallback : DIRECTORY_DEFAULT_BASEMAP;
}

/** Leaflet's `baselayerchange` reports the layer's name; turn it back into an id. */
export function basemapIdFromLabel(label) {
  return BASEMAPS.find((b) => b.label === label)?.id ?? null;
}
